import { useState, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { Heart, Sparkles } from 'lucide-react';
import { RUNAWAY_QUESTION } from '@/config/valentineContent';

export default function RunawayQuestion() {
  const [answered, setAnswered] = useState(false);
  const [noPos, setNoPos] = useState({ x: 0, y: 0 });
  const [dodgeCount, setDodgeCount] = useState(0);
  const containerRef = useRef(null);

  const dodgeNo = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    // Keep button within container bounds (with padding)
    const maxX = rect.width - 120;
    const maxY = rect.height - 60;
    const x = Math.random() * maxX - maxX / 2;
    const y = Math.random() * maxY - maxY / 2;
    setNoPos({ x, y });
    setDodgeCount(c => c + 1);
  }, []);

  const handleYes = () => {
    setAnswered(true);
    const colors = ['#E91E63', '#F48FB1', '#FF80AB', '#FCE4EC', '#CE93D8', '#fff'];
    confetti({ particleCount: 80, spread: 100, origin: { y: 0.6 }, colors });
    setTimeout(() => confetti({ particleCount: 40, spread: 70, origin: { y: 0.65 }, colors }), 250);
  };

  // Scale down the No button as it dodges more
  const noScale = Math.max(0.4, 1 - dodgeCount * 0.08);
  const noOpacity = Math.max(0.3, 1 - dodgeCount * 0.06);

  return (
    <section className="section-romantic text-center relative overflow-hidden">
      <div className="max-w-lg mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, var(--accent-light))' }} />
          <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, var(--accent-light), transparent)' }} />
        </div>

        <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
          {RUNAWAY_QUESTION.sectionLabel}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ color: 'var(--text-primary)' }}>
          {RUNAWAY_QUESTION.heading}
        </h2>
        <p className="font-body text-xs mb-10" style={{ color: 'var(--text-secondary)' }}>
          {RUNAWAY_QUESTION.subtitle}
        </p>

        {!answered ? (
          <div ref={containerRef} className="relative flex items-center justify-center gap-6" style={{ minHeight: 160 }}>
            {/* Yes button — always in place */}
            <button
              onClick={handleYes}
              className="px-8 py-3.5 rounded-2xl font-body text-base font-medium transition-all duration-200 hover:scale-110 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, var(--accent-primary), #C2185B)',
                color: '#fff',
                boxShadow: '0 4px 20px var(--accent-glow)',
                cursor: 'pointer',
                border: 'none',
                zIndex: 2,
              }}
            >
              <span className="flex items-center gap-2">
                <Heart size={16} fill="#fff" strokeWidth={0} />
                {RUNAWAY_QUESTION.yesButton}
              </span>
            </button>

            {/* No button — runs away */}
            <button
              onMouseEnter={dodgeNo}
              onTouchStart={dodgeNo}
              onClick={dodgeNo}
              className="px-8 py-3.5 rounded-2xl font-body text-base font-medium"
              style={{
                background: 'none',
                color: 'var(--text-secondary)',
                border: '1px solid var(--accent-glow)',
                cursor: 'pointer',
                position: 'relative',
                transform: `translate(${noPos.x}px, ${noPos.y}px) scale(${noScale})`,
                opacity: noOpacity,
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s, font-size 0.3s',
                zIndex: 1,
              }}
            >
              {RUNAWAY_QUESTION.noButton}
            </button>

            {/* Hints after dodges */}
            {dodgeCount >= 2 && dodgeCount < 5 && (
              <p className="absolute bottom-0 left-0 right-0 font-body text-[10px] animate-pulse" style={{ color: 'var(--accent-light)' }}>
                The "No" button seems to be running away…
              </p>
            )}
            {dodgeCount >= 5 && (
              <p className="absolute bottom-0 left-0 right-0 font-body text-[10px] animate-pulse" style={{ color: 'var(--accent-light)' }}>
                Just say yes already!
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-2xl p-8 mx-auto max-w-sm relative overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--accent-glow)',
              boxShadow: '0 8px 40px var(--accent-glow)',
              animation: 'fadeSlideUp 0.5s ease-out',
            }}>
            <span className="text-5xl block mb-3">{RUNAWAY_QUESTION.celebration.emoji}</span>
            <p className="font-heading text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>
              {RUNAWAY_QUESTION.celebration.message}
            </p>
            <p className="font-body text-sm" style={{ color: 'var(--text-secondary)' }}>
              {RUNAWAY_QUESTION.celebration.submessage}
            </p>
            <div className="mt-4">
              <Heart size={24} fill="var(--accent-primary)" strokeWidth={0} className="inline-block" style={{ animation: 'heartPulse 1s ease-in-out infinite' }} />
            </div>
            <div className="absolute top-3 left-3 opacity-20" style={{ color: 'var(--accent-light)' }}><Sparkles size={12} /></div>
            <div className="absolute bottom-3 right-3 opacity-20" style={{ color: 'var(--accent-light)' }}><Sparkles size={12} /></div>
          </div>
        )}
      </div>
    </section>
  );
}
